/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

var MarqueeText = class extends HTMLElement {
  constructor() {
    super();
    this._scroller = null;
    this._initialize = null;

    this.initialize();
  }
  direction() {
    return this.getAttribute("direction") === "right" ? 1 : -1;
  }
  scroller() {
    return this.shadowRoot.querySelector('[part="scroller"]');
  }
  initialize() {
    this.attachShadow({ mode: "open" }).appendChild(document.createRange().createContextualFragment(`
        <slot part="scroller"></slot>
      `));
    const fragment = document.createDocumentFragment();
    for (let i = 1; i <= 5; ++i) {
      const node = this.firstElementChild.cloneNode(true);
      node.setAttribute("aria-hidden", "true");
      node.style.cssText = `position: absolute; inset-inline-start: ${100 * i * - this.direction()}%;`;
      fragment.appendChild(node);
    }
    this.append(fragment);
    
    this.scroller().animate(
      {
        transform: ["translateX(0)", `translateX(calc(var(--transform-logical-flip) * ${this.direction() * 100}%))`]
      },{
        duration: 1 / parseFloat(this.getAttribute("speed")) * (this.scroller().clientWidth),
        iterations: Infinity
      }
    );
  }
};
if (!window.customElements.get("marquee-text")) {
  window.customElements.define("marquee-text", MarqueeText);
}

var BundleProducts = class extends HTMLElement {
  constructor() {
    super();

    this.multiple = this.getAttribute('data-multiple') === 'true';
    this.inputs = this.querySelectorAll('input[type="checkbox"], input[type="radio"]');

    this.quantityInput = document.querySelector('quantity-selector .quantity-selector__input');

    this.currentTitle = this.querySelector('.bundle-products--title__current');
    this.currentPrice = this.querySelector('.bundle-products--title__current-price');
    
    this.moneyFormat = new DOMParser().parseFromString(window.themeVariables.settings.moneyFormat, 'text/html').body.innerText;

    for (const input of this.inputs) {
      input.addEventListener('change', this.handleChange.bind(this));
    }
    if (this.quantityInput) {
      this.quantityInput.addEventListener('input:changed', this.handleQuantityChange.bind(this));
    }
  }

  handleChange(event) {
    if (!this.multiple) {      
      for (const input of this.inputs) {
        input.previousElementSibling.disabled = true;
      }
    }

    event.currentTarget.previousElementSibling.disabled = !event.currentTarget.checked;

    if (this.multiple) {
      let totalPrice = 0;
      let itemCount = 0;

      for (const input of this.inputs) {
        if (input.checked) {
          itemCount++;
          totalPrice += parseFloat(input.dataset['price'])
        }
      }

      if (this.currentTitle) {
        this.currentTitle.innerText = `${itemCount} ${this.currentTitle.dataset['selected']}`;
      }
      
      if (this.currentPrice) {
        this.currentPrice.innerText = `(${window.formatMoney(totalPrice, this.moneyFormat)})`;
      }

      this.setAttribute('data-price', totalPrice);
    } else {
      if (this.currentTitle) {
        this.currentTitle.innerText = event.currentTarget.dataset['title'];
      }
      
      if (this.currentPrice) {
        this.currentPrice.innerText = `(${window.formatMoney(parseFloat(event.currentTarget.dataset['price']), this.moneyFormat)})`;
      }

      this.setAttribute('data-price', event.currentTarget.dataset['price']);
    }

    document.dispatchEvent(new CustomEvent('final-price:update'));

    const customSetsParent = this.closest('custom-set');

    if (customSetsParent) {
      customSetsParent.dispatchEvent(new CustomEvent('showOtherOptions', {
        detail: {
          isMainBlock: customSetsParent.hasAttribute('data-main-block')
        }
      }));

      customSetsParent.dispatchEvent(new CustomEvent('verifyAddToCart'));
    }
  }

  handleQuantityChange(event) {
    for (const input of this.inputs) {
      input.previousElementSibling.value = event.detail.input.quantity;
    }
  }
}

var CustomSet = class extends HTMLElement {
  constructor() {
    super();

    this.buttons = this.querySelectorAll('.bundle-products--tab-button');
    this.tabs = this.querySelectorAll('bundle-products');

    this.allCustomSets = document.querySelectorAll('custom-set:not([data-multiple="true"])');

    this.buyButton = document.getElementById('AddToCart');

    this.sectionId = this.getAttribute('data-section-id');

    for (const button of this.buttons) {
      button.addEventListener('click', this.switch.bind(this));
    }

    this.mainBlock = this.hasAttribute('data-main-block');

    if (this.mainBlock) {
      this.alreadyShown = false;
      this.addEventListener('showOtherOptions', function(e) {
        this.showOtherOptions(e);
      }.bind(this));
    }

    this.addEventListener('verifyAddToCart', this.verifyAddToCart.bind(this));
  }

  switch(event) {
    const button = event.target.closest('.bundle-products--tab-button');

    for (const tab of this.tabs) {
      if (button.getAttribute('data-for').trim() === tab.getAttribute('data-id').trim()) {
        tab.classList.remove('hidden');
        this.setDisabled(tab, false);
      } else {
        tab.classList.add('hidden');
        this.setDisabled(tab, true);
      }
    }
  }

  showOtherOptions(event) {
    if (this.alreadyShown === true || event.detail.isMainBlock !== true) {
      return;
    }

    // Show initially hidden custom sets
    const hiddenCustomSets = document.querySelectorAll('custom-set:not([data-main-block])[hidden]');

    for (const customSet of hiddenCustomSets) {
      customSet.removeAttribute('hidden');
    }

    // By default, because there is no selection, the buy button is disabled, so we need to enable it
    const disabledBuyButton = document.querySelector('.product-form__add-button[disabled]');

    if (disabledBuyButton) {
      disabledBuyButton.removeAttribute('disabled');
    }

    // Since flickities are hidden initially, we need to resize them after they are shown
    const innerFlickities = document.querySelectorAll('.bundle-products--inner[data-flickity]');

    for (const innerFlickity of innerFlickities) {
      if (window.ThemeFlickity && window.ThemeFlickity.data) {
        window.ThemeFlickity.data(innerFlickity).resize();
      }
    }

    // Show prices
    const initiallyHiddenItems = document.querySelectorAll('[data-initially-hidden]');

    for (const item of initiallyHiddenItems) {
      item.removeAttribute('data-initially-hidden');
    }

    // Set state, so we don't do all this again
    this.alreadyShown = true;
  }

  setDisabled(tab, state) {
    for (const input of tab.inputs) {
      if (state) {
        // If it's going to be disabled, save the last state whether it was checked or not
        if (input.checked) {
          tab.setAttribute('data-state', input.id);
          input.checked = false;
          input.dispatchEvent(new Event('change'));
        }
      }
      input.disabled = state;

      if (!state) {
        // If it's going to be enabled, restore the preserved check state
        if (tab.getAttribute('data-state') == input.id) {
          input.checked = true;
          input.dispatchEvent(new Event('change'));
        }
      }
    }

    /*
    if (!state) {
      let anyChecked = false;
      for (const input of tab.inputs) {
        if (input.checked) {
          anyChecked = true;
          break;
        }
      }

      if (!anyChecked) {
        tab.inputs[0].checked = true;
        tab.inputs[0].dispatchEvent(new Event('change'));
      }
    }
    */
  }

  getCheckoutHideStyle() {
    let style = document.getElementById('hide-buy-button');
    let created = false;

    if (style === null) {
      created = true;

      style = document.createElement('style');
      style.id = 'hide-buy-button';
      style.innerHTML = `#shopify-section-${this.sectionId} .shopify-payment-button { display: none; }`;
    }
    
    return {
      created,
      style
    }
  }

  verifyAddToCart() {
    const checkoutHidingStyle = this.getCheckoutHideStyle();

    for (const customSet of this.allCustomSets) {
      const currentBundle = customSet.querySelector('.bundle-products:not(.hidden) input:checked');

      if (currentBundle === null) {
        this.buyButton.classList.add('button--ternary');
        this.buyButton.classList.remove('button--primary');
        this.buyButton.setAttribute('disabled', 'disabled');
        
        if (checkoutHidingStyle.created) {
          this.buyButton.insertAdjacentElement('afterend', checkoutHidingStyle.style);
        }
        return;
      }
    }

    this.buyButton.classList.remove('button--ternary');
    this.buyButton.classList.add('button--primary');
    this.buyButton.removeAttribute('disabled');

    checkoutHidingStyle.style.remove();
  }
}

var CustomSetsTabs = class extends HTMLElement {
  constructor() {
    super();

    this.buttons = this.querySelectorAll('.bundle-products--tab-button');
    this.currentlySelected = this.querySelector('.bundle-products--title__current');

    for (const button of this.buttons) {
      button.addEventListener('click', this.switch.bind(this));
    }
  }

  switch(event) {
    const current = event.target.closest('.bundle-products--tab-button');

    for (const button of this.buttons) {
      button.classList.remove('bundle-products--tab-button--current');
    }

    current.classList.add('bundle-products--tab-button--current');
    const currentlySelectedTab = current.innerText
    this.currentlySelected.innerText = currentlySelectedTab;

    const allTabs = document.querySelectorAll('.bundle-products--tab-button[data-for]');

    for (const tab of allTabs) {
      if (tab.innerText === currentlySelectedTab) {
        tab.click();
      }
    }
  }
}

var CustomSetsFinalPrice = class extends HTMLElement {
  constructor() {
    super();

    this.moneyFormat = new DOMParser().parseFromString(window.themeVariables.settings.moneyFormat, 'text/html').body.innerText;

    document.addEventListener('final-price:update', this.updateFinalPrice.bind(this));
    document.addEventListener('variant:changed', function (e) {
      this.updateFinalPrice(e);
    }.bind(this));

    this.updateFinalPrice();
  }

  updateFinalPrice(event = null) {
    let totalPrice = 0;

    /*
    // The selected variant has a [data-price] field as well, so this part is redundant.
    if (event !== null && event.detail && event.detail.variant) {
      totalPrice = event.detail.variant.price;
    } else {
      const originalPrice = document.querySelector('[data-original-price]');

      if (originalPrice) {
        totalPrice = parseFloat(originalPrice.getAttribute('data-original-price'));
      }
    }
    */

    const bundlePrices = document.querySelectorAll('input:checked[data-price]');

    for (const bundlePrice of bundlePrices) {
      totalPrice += Number(bundlePrice.getAttribute('data-price'));
    }

    this.innerText = window.formatMoney(totalPrice, this.moneyFormat);
  }
}

if (!window.customElements.get("bundle-products")) {
  window.customElements.define("bundle-products", BundleProducts);
}

if (!window.customElements.get("custom-set")) {
  window.customElements.define("custom-set", CustomSet);
}

if (!window.customElements.get("custom-sets-tabs")) {
  window.customElements.define("custom-sets-tabs", CustomSetsTabs);
}

if (!window.customElements.get("custom-sets-final-price")) {
  window.customElements.define("custom-sets-final-price", CustomSetsFinalPrice);
}